/**
 * The numbers under a draft — "412 words · 3 to-dos · 8 sources" — shared by
 * the section editor footer and the Full response reader footer.
 *
 * Both are counted from the draft CONTENT the page already holds, not from a
 * server list: the Submission Pack's asks are a bid-wide snapshot that does not
 * move while someone types, and `[ATT: …]` placeholders are not pack asks at
 * all. Pure functions, no network.
 */

import { findAnnotations, outermostAnnotations } from '@/lib/annotations'
import { displayCitationPage } from '@/lib/citation-preview'
import type { CitationRef } from '@/lib/api'

// ===================
// To-dos
// ===================

/**
 * Open to-dos in document order, as the raw matched tokens: every `[TODO …]`
 * note and every `[ATT: …]` attachment placeholder. An `[ATT: …]` inside a
 * note is part of that note and is not counted twice (`outermostAnnotations`).
 * Legacy "(from your attached documents: …)" prose is not a to-do.
 */
export function findDraftToDos(content: string): string[] {
  return outermostAnnotations(findAnnotations(content))
    .filter((annotation) => annotation.type === 'todo' || annotation.type === 'attachment')
    .map((annotation) => annotation.match)
}

/** The to-dos half of a stats line: how many, and how to jump to the first. */
export interface DraftToDosStat {
  count: number
  /** Omitted when there is nothing on screen to jump to. */
  onJump?: (() => void) | undefined
}

/**
 * A to-do token as it reads on screen. Stored content may be HTML, so an `&`
 * in a note arrives as `&amp;` (and inline tags may sit inside the payload);
 * the rendered text a jump searches for carries neither. DOMParser documents
 * are inert — nothing in the token runs or loads.
 */
export function tokenDisplayText(token: string): string {
  if (!/[&<]/.test(token)) return token
  return new DOMParser().parseFromString(token, 'text/html').documentElement.textContent ?? token
}

// ===================
// Sources
// ===================

export type CitedSourceKind = 'tender' | 'library'

/** One document a draft cites, however many times and at however many pages. */
export interface CitedSource {
  documentId: string
  documentName: string
  kind: CitedSourceKind
  /** Every cited ref to this document, lowest page first (unknown pages last). */
  refs: CitationRef[]
  /** Distinct display page numbers, ascending. Empty when no ref has a page. */
  pages: number[]
}

/** A draft part the refs belong to: one segment in the editor. */
export interface CitingContent {
  content: string
  citationRefs?: CitationRef[] | null
  libraryCitationRefs?: CitationRef[] | null
}

// The marker shapes the editor makes clickable (citationDecorationPlugin.ts):
// strict [N] / [LN], comma runs [1, 3] / [L1, L3], and annotated [1 Name].
const CITATION_BRACKET_REGEX = /\[(L?\d+(?:\s*,\s*L?\d+)*)\]/g
const ANNOTATED_CITATION_REGEX = /\[(L?)(\d+)\s+[^\]\n]+\]/g

/** Citation indices that actually appear in the text, by kind. */
export function citedIndices(content: string): Record<CitedSourceKind, Set<number>> {
  const found: Record<CitedSourceKind, Set<number>> = { tender: new Set(), library: new Set() }
  if (!content) return found

  for (const match of content.matchAll(CITATION_BRACKET_REGEX)) {
    const body = match[1] ?? ''
    // A comma run takes its kind from the first entry, as the editor does:
    // "[L1, 3]" is two library citations.
    const isLibrary = body.startsWith('L')
    for (const part of body.split(',')) {
      const index = parseInt(part.trim().replace(/^L/, ''), 10)
      if (index >= 1) found[isLibrary ? 'library' : 'tender'].add(index)
    }
  }
  for (const match of content.matchAll(ANNOTATED_CITATION_REGEX)) {
    const index = parseInt(match[2] ?? '', 10)
    if (index >= 1) found[match[1] === 'L' ? 'library' : 'tender'].add(index)
  }
  return found
}

function byPage(a: CitationRef, b: CitationRef): number {
  const pa = displayCitationPage(a.pageNumber)
  const pb = displayCitationPage(b.pageNumber)
  if (pa === pb) return a.citationIndex - b.citationIndex
  if (pa === null) return 1
  if (pb === null) return -1
  return pa - pb
}

/**
 * The distinct documents a draft cites, tender and library together,
 * de-duplicated by document and sorted by name.
 *
 * A ref counts only when its marker is still in the text — refs are recorded
 * when a draft is written, and a writer can delete the sentence that carried
 * one. Each part's refs are matched against that part's own content, because
 * citation numbers restart per segment.
 */
export function collectCitedSources(parts: readonly CitingContent[]): CitedSource[] {
  const byDocument = new Map<string, CitedSource>()

  const add = (ref: CitationRef, kind: CitedSourceKind) => {
    const existing = byDocument.get(ref.documentId)
    if (existing) {
      existing.refs.push(ref)
      return
    }
    byDocument.set(ref.documentId, {
      documentId: ref.documentId,
      documentName: ref.documentName,
      kind,
      refs: [ref],
      pages: [],
    })
  }

  for (const part of parts) {
    const indices = citedIndices(part.content)
    for (const ref of part.citationRefs ?? []) {
      if (indices.tender.has(ref.citationIndex)) add(ref, 'tender')
    }
    for (const ref of part.libraryCitationRefs ?? []) {
      if (indices.library.has(ref.citationIndex)) add(ref, 'library')
    }
  }

  const sources = [...byDocument.values()]
  for (const source of sources) {
    source.refs.sort(byPage)
    const pages = source.refs
      .map((ref) => displayCitationPage(ref.pageNumber))
      .filter((page): page is number => page !== null)
    source.pages = [...new Set(pages)]
  }
  return sources.sort((a, b) => a.documentName.localeCompare(b.documentName))
}
